import React, { useId, useRef, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  SegmentedControl,
  Textarea,
} from '../../../components/ui';
import { issuesErrorMessage, recordStance } from '../issuesApi';
import type { RecordStanceInput, StanceConfidence, StanceSource, StanceView, SubjectRef } from '../../../types/issues';
import { CONFIDENCES, CONFIDENCE_LABEL, SOURCE_HINT, SOURCE_LABEL } from './format';
import { PositionChange, PositionPicker } from './PositionMark';

export interface StanceDefaults {
  issueInfluence?: number;
  confidence?: StanceConfidence;
  source?: StanceSource;
  rationale?: string | null;
  evidenceUrl?: string | null;
  signalId?: string | null;
}

interface RecordStanceDialogProps {
  issueId: string;
  subject: SubjectRef;
  /** What is on record now, null when the entity has never been placed */
  from: number | null;
  /** Where the drag or the menu is putting them */
  position: number;
  defaults?: StanceDefaults;
  onClose: () => void;
  onRecorded: (stance: StanceView) => void;
}

/** Sources an analyst can choose; `ai` is only ever set by accepting a proposal */
const MANUAL_SOURCES: StanceSource[] = ['stated', 'observed', 'inferred'];

/**
 * The confirm step between moving a card and changing the record. Nothing is
 * written until the analyst says what the move rests on — a reading with no
 * rationale is worse than no reading at all.
 */
export const RecordStanceDialog: React.FC<RecordStanceDialogProps> = ({
  issueId,
  subject,
  from,
  position: initialPosition,
  defaults,
  onClose,
  onRecorded,
}) => {
  const titleId = useId();
  const rationaleRef = useRef<HTMLTextAreaElement>(null);
  const [position, setPosition] = useState(initialPosition);
  const [rationale, setRationale] = useState(defaults?.rationale ?? '');
  const [confidence, setConfidence] = useState<StanceConfidence>(defaults?.confidence ?? 'medium');
  const [source, setSource] = useState<StanceSource>(
    defaults?.source && MANUAL_SOURCES.includes(defaults.source) ? defaults.source : 'observed',
  );
  const [evidenceUrl, setEvidenceUrl] = useState(defaults?.evidenceUrl ?? '');
  const [influence, setInfluence] = useState(Math.round(defaults?.issueInfluence ?? 50));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const reason = rationale.trim();
    if (!reason) {
      setError('Say what this reading rests on');
      rationaleRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    const input: RecordStanceInput = {
      subjectType: subject.type,
      subjectId: subject.id,
      position,
      issueInfluence: influence,
      confidence,
      source,
      rationale: reason,
      evidenceUrl: evidenceUrl.trim() || null,
      ...(defaults?.signalId ? { signalId: defaults.signalId } : {}),
    };
    try {
      onRecorded(await recordStance(issueId, input));
    } catch (err) {
      setError(issuesErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy={titleId} handle initialFocusRef={rationaleRef} className="max-h-[92dvh] sm:max-w-lg">
      <DialogHeader
        id={titleId}
        eyebrow={from === null ? 'First position' : 'Move position'}
        title={subject.label}
        onClose={onClose}
      />
      <DialogBody className="space-y-3.5">
        <div className="flex items-center gap-3 rounded-2xl bg-neutral-50 px-3.5 py-3">
          <Avatar name={subject.label} kind={subject.type} size="md" />
          <div className="min-w-0">
            <PositionChange from={from} to={position} />
            <p className="mt-1 text-[12px] text-neutral-400">
              {subject.type === 'organization' ? 'Organization' : 'Person'} · recorded as a new reading, the history is kept
            </p>
          </div>
        </div>

        <Field label="Position">
          <PositionPicker value={position} onChange={setPosition} label="Position on this issue" />
        </Field>

        <Field label="Why" htmlFor={`${titleId}-why`} hint="One line another analyst could check" required>
          <Textarea
            id={`${titleId}-why`}
            ref={rationaleRef}
            rows={3}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Said at the Conakry hearing that the permit should proceed with a revised royalty"
          />
        </Field>

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Field label="Confidence">
            <SegmentedControl
              label="Confidence"
              value={confidence}
              onChange={(value) => setConfidence(value as StanceConfidence)}
              items={CONFIDENCES.map((value) => ({ value, label: CONFIDENCE_LABEL[value] }))}
              className="w-full"
            />
          </Field>
          <Field label="Source" hint={SOURCE_HINT[source]}>
            <SegmentedControl
              label="Source"
              value={source}
              onChange={(value) => setSource(value as StanceSource)}
              items={MANUAL_SOURCES.map((value) => ({ value, label: SOURCE_LABEL[value] }))}
              className="w-full"
            />
          </Field>
        </div>

        <Field label="Evidence" htmlFor={`${titleId}-evidence`} hint="A link to what was said or done">
          <Input
            id={`${titleId}-evidence`}
            type="url"
            inputMode="url"
            value={evidenceUrl}
            onChange={(e) => setEvidenceUrl(e.target.value)}
            placeholder="https://"
          />
        </Field>

        <Field
          label="Influence on this issue"
          htmlFor={`${titleId}-influence`}
          hint="How much this actor can affect the outcome, not their standing in general"
        >
          <div className="flex items-center gap-3">
            <input
              id={`${titleId}-influence`}
              type="range"
              min={0}
              max={100}
              step={5}
              value={influence}
              onChange={(e) => setInfluence(Number(e.target.value))}
              className="h-9 flex-1 accent-primary-600"
            />
            <span className="w-8 flex-shrink-0 text-right text-[13px] tabular-nums text-ink">{influence}</span>
          </div>
        </Field>

        {error && (
          <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button size="sm" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" loading={saving} onClick={save}>
          Record
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default RecordStanceDialog;
